import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Activity,
  Sparkles,
  Zap,
  Shield,
  Github,
  Play,
  ArrowRight,
  CheckCircle,
  Cpu,
  BarChart3,
  Lock,
  Code2,
  Terminal,
  Globe,
  Layers,
  PieChart,
  TrendingUp,
  Users,
  Clock,
  Bell,
  Database,
  Cloud,
  Server,
  Layout,
  Settings,
  ChevronRight,
  Braces,
  GitBranch,
  Video,
  Gauge,
  Bug,
  Brain,
  Wrench,
} from "lucide-react";

function Landing() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-[#0a0a0f] overflow-x-hidden"
    >
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Gradient Mesh */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(6,182,212,0.08),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_50%,rgba(99,102,241,0.06),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(6,182,212,0.05),transparent_60%)]" />

        {/* Animated Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:80px_80px]" />

        {/* Floating Particles */}
        <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-cyan-400/40 rounded-full animate-float" />
        <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 bg-blue-400/30 rounded-full animate-float-slow" />
        <div className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-purple-400/30 rounded-full animate-float" />
        <div className="absolute bottom-1/4 right-1/3 w-1.5 h-1.5 bg-cyan-400/20 rounded-full animate-float-slow" />
      </div>

      {/* Navbar */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0a0f]/80 backdrop-blur-xl"
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg blur-xl opacity-50 animate-pulse" />
              <Activity className="w-8 h-8 text-cyan-400 relative" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">
              FrankTech
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a
              href="#features"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              Features
            </a>
            <a
              href="#sdk"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              SDK
            </a>
            <a
              href="#pricing"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              Pricing
            </a>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-gray-400 hover:text-white transition-colors text-sm"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 text-sm font-medium flex items-center gap-2 group"
            >
              Get Started
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative z-10"
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/20 bg-cyan-500/10 text-cyan-400 text-xs font-medium tracking-wider uppercase mb-6">
                <Sparkles className="w-3 h-3" />
                V-2.0
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.1]">
                FrankTech SDK
                <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent mt-2">
                  AI-Powered Error Intelligence
                </span>
              </h1>

              <p className="text-lg text-gray-400 mt-6 max-w-lg leading-relaxed">
                Monitor, analyze, and fix errors automatically with session
                replay and GitHub integration. One-line setup, instant insights.
              </p>

              <div className="flex flex-wrap gap-4 mt-10">
                <Link
                  to="/register"
                  className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-cyan-500/30 transition-all duration-300 font-medium flex items-center gap-2 group"
                >
                  Get Started
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="https://github.com/frankiekoifi/franktech-sdk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-300 font-medium flex items-center gap-2 group"
                >
                  <Github className="w-4 h-4" />
                  View Docs
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              <div className="flex flex-wrap gap-8 mt-10 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  No credit card
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  14-day trial
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                  Cancel anytime
                </span>
              </div>
            </motion.div>

            {/* Right Column - Dashboard Mockups */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative h-[500px] lg:h-[600px] hidden lg:block"
            >
              {/* Dashboard 1 - Main */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute top-0 right-0 w-[380px] bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl shadow-cyan-500/10 rotate-[-2deg] hover:rotate-0 transition-all duration-500"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="text-xs text-gray-500 ml-2">
                    FrankTech Dashboard
                  </span>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/20 flex items-center justify-center">
                        <Activity className="w-4 h-4 text-cyan-400" />
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">
                          Error Rate
                        </p>
                        <p className="text-xs text-gray-500">
                          12.4% lower than yesterday
                        </p>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-white">2.3%</span>
                  </div>
                  <div className="h-20 flex items-end gap-1">
                    {[40, 65, 45, 80, 55, 70, 60, 85, 50, 75, 65, 90].map(
                      (h, i) => (
                        <div
                          key={i}
                          className="flex-1 bg-gradient-to-t from-cyan-500/40 to-blue-600/40 rounded-sm hover:opacity-80 transition-all"
                          style={{ height: `${h}%` }}
                        />
                      ),
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Mon</span>
                    <span>Tue</span>
                    <span>Wed</span>
                    <span>Thu</span>
                    <span>Fri</span>
                    <span>Sat</span>
                    <span>Sun</span>
                  </div>
                </div>
              </motion.div>

              {/* Dashboard 2 - Stats */}
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{
                  duration: 7,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
                className="absolute top-20 left-0 w-[250px] bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5 shadow-2xl shadow-blue-500/10 rotate-[4deg] hover:rotate-0 transition-all duration-500"
              >
                <div className="flex items-center gap-2 mb-4">
                  <PieChart className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-white font-medium">
                    Performance
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">API Response</span>
                    <span className="text-white font-medium">124ms</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-1.5">
                    <div className="w-3/4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full h-1.5" />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Error Rate</span>
                    <span className="text-green-400 font-medium">0.02%</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-1.5">
                    <div className="w-1/4 bg-green-500 rounded-full h-1.5" />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Uptime</span>
                    <span className="text-cyan-400 font-medium">99.99%</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-1.5">
                    <div className="w-[99%] bg-cyan-500 rounded-full h-1.5" />
                  </div>
                </div>
              </motion.div>

              {/* Dashboard 3 - Alerts */}
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5,
                }}
                className="absolute bottom-10 right-0 w-[200px] bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-2xl shadow-purple-500/10 rotate-[-5deg] hover:rotate-0 transition-all duration-500"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Bell className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs text-white font-medium">
                    Recent Alerts
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-gray-400">API timeout</span>
                    <span className="text-gray-600 ml-auto">2m ago</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                    <span className="text-gray-400">Slow query</span>
                    <span className="text-gray-600 ml-auto">15m ago</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-gray-400">Memory leak</span>
                    <span className="text-gray-600 ml-auto">1h ago</span>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-cyan-400 text-sm font-medium tracking-wider uppercase">
              Features
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">
              Everything You Need to <br className="hidden md:block" />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Monitor & Fix Errors
              </span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Feature 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="group bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 rounded-2xl p-8 transition-all hover:bg-white/[0.06] hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all">
                <Bug className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                Error Tracking
              </h3>
              <p className="text-gray-400 text-sm mt-2">
                Automatically capture errors from your applications with
                one-line setup.
              </p>
            </motion.div>

            {/* Feature 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="group bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 rounded-2xl p-8 transition-all hover:bg-white/[0.06] hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all">
                <Brain className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                AI Root Cause Analysis
              </h3>
              <p className="text-gray-400 text-sm mt-2">
                Get instant root cause analysis and suggested fixes with
                confidence scores.
              </p>
            </motion.div>

            {/* Feature 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="group bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 rounded-2xl p-8 transition-all hover:bg-white/[0.06] hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all">
                <Video className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                Session Replay
              </h3>
              <p className="text-gray-400 text-sm mt-2">
                Watch exactly what users did before an error occurred. Replay
                like a video.
              </p>
            </motion.div>

            {/* Feature 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="group bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 rounded-2xl p-8 transition-all hover:bg-white/[0.06] hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all">
                <Gauge className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                Performance Metrics
              </h3>
              <p className="text-gray-400 text-sm mt-2">
                Track API response times, page load metrics, and slow requests
                in real-time.
              </p>
            </motion.div>

            {/* Feature 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
              className="group bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 rounded-2xl p-8 transition-all hover:bg-white/[0.06] hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all">
                <Wrench className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                GitHub Automation
              </h3>
              <p className="text-gray-400 text-sm mt-2">
                AI-generated fixes are automatically pushed as GitHub PRs.
                Review and merge with one click.
              </p>
            </motion.div>

            {/* Feature 6 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
              className="group bg-white/[0.03] border border-white/5 hover:border-cyan-500/30 rounded-2xl p-8 transition-all hover:bg-white/[0.06] hover:-translate-y-1"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-all">
                <Code2 className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">
                One-Line Setup
              </h3>
              <p className="text-gray-400 text-sm mt-2">
                Install the SDK and start monitoring in seconds. Works with
                React, Node.js, Express, and more.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SDK Section */}
      <section id="sdk" className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-cyan-400 text-sm font-medium tracking-wider uppercase">
                SDK
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">
                Install in Seconds
              </h2>
              <p className="text-gray-400 mt-4 text-lg leading-relaxed">
                Get started with FrankTech in just a few lines of code. No
                complex configuration, just one command and you're ready.
              </p>
              <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-3 text-sm text-gray-400">
                  <Terminal className="w-4 h-4 text-cyan-400" />
                  <span>$ npm install @franktech/sdk</span>
                </div>
              </div>
              <div className="mt-4 bg-white/5 border border-white/10 rounded-2xl p-6">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`import FrankTech from '@franktech/sdk';

const monitor = new FrankTech({
  apiKey: 'ft_your_api_key',
  endpoint: 'https://franktech-api.franktechspace.dev'
});`}</code>
                </pre>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl shadow-cyan-500/5">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="text-xs text-gray-500 ml-2">
                    FrankTech SDK
                  </span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <div>
                      <p className="text-sm text-white font-medium">
                        TypeError: Cannot read property 'x'
                      </p>
                      <p className="text-xs text-gray-500">
                        2 min ago • production
                      </p>
                    </div>
                    <span className="ml-auto text-xs text-cyan-400">
                      AI Analyzing...
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                    <div>
                      <p className="text-sm text-white font-medium">
                        API timeout in /api/users
                      </p>
                      <p className="text-xs text-gray-500">
                        15 min ago • staging
                      </p>
                    </div>
                    <span className="ml-auto text-xs text-green-400">
                      Fixed ✅
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <div>
                      <p className="text-sm text-white font-medium">
                        Memory leak in auth module
                      </p>
                      <p className="text-xs text-gray-500">
                        1 hour ago • production
                      </p>
                    </div>
                    <span className="ml-auto text-xs text-cyan-400">
                      PR Created
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="pricing" className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-500/20 rounded-3xl p-12 md:p-16 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Ready to Stop Debugging Manually?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mt-4 text-lg">
              Join thousands of developers using FrankTech to catch and fix
              errors before their users notice.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Link
                to="/register"
                className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-cyan-500/30 transition-all duration-300 font-medium flex items-center gap-2 group"
              >
                Start Free Trial
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="https://github.com/frankiekoifi/franktech-sdk"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-300 font-medium flex items-center gap-2 group"
              >
                <Github className="w-4 h-4" />
                Star on GitHub
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#0a0a0f]/80">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2">
                <Activity className="w-6 h-6 text-cyan-400" />
                <span className="text-lg font-bold text-white">FrankTech</span>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                AI-powered error monitoring for modern developers.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>
                  <Link
                    to="/register"
                    className="hover:text-gray-300 transition-colors"
                  >
                    Sign Up
                  </Link>
                </li>
                <li>
                  <a
                    href="https://github.com/frankiekoifi/franktech-sdk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-300 transition-colors"
                  >
                    SDK
                  </a>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="hover:text-gray-300 transition-colors"
                  >
                    Sign In
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-gray-300 transition-colors"
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-gray-300 transition-colors"
                  >
                    Privacy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Connect</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>
                  <a
                    href="https://github.com/frankiekoifi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-300 transition-colors"
                  >
                    GitHub
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:support@franktechspace.dev"
                    className="hover:text-gray-300 transition-colors"
                  >
                    support@franktechspace.dev
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 mt-8 pt-8 text-center text-sm text-gray-600">
            © 2026 FrankTech Intelligence. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Add animation keyframes to your global CSS */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px);
            opacity: 0.6;
          }
        }
        @keyframes float-slow {
          0%,
          100% {
            transform: translateY(0px);
            opacity: 0.2;
          }
          50% {
            transform: translateY(-30px);
            opacity: 0.5;
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default Landing;
